import { decodeAbiParameters, AbiParameter, isAddressEqual } from 'viem';

import { EASAttestation, Hash } from '@/types';
import {
  useReadEasGetAttestation,
  useReadIAttestationIndexerGetAttestationUid,
} from '@generated/generated';
import { DEFAULT_WAGMI_QUERY_ATTESTATION_STALE_TIME } from '@/onchain/querySettings';

export type UseAttestationRequest = {
  address: `0x${string}`;
  schemaUid: `0x${string}`;
  abiParams?: AbiParameter[];
};

export type UseEasAttestationResponse = {
  attestation: EASAttestation;
  isLoading: boolean;
};

export function useEasAttestation({
  address,
  schemaUid,
  abiParams,
}: UseAttestationRequest): UseEasAttestationResponse {
  const { data: attestationUidData, isLoading: isLoadingUid } =
    useReadIAttestationIndexerGetAttestationUid({
      args: [address, schemaUid],
    });
  const attestationUid = (attestationUidData as Hash) ?? '0x0';

  const { data: attestation, isLoading: isLoadingAttestation } = useReadEasGetAttestation({
    args: [attestationUid],
    query: {
      staleTime: DEFAULT_WAGMI_QUERY_ATTESTATION_STALE_TIME,
      gcTime: DEFAULT_WAGMI_QUERY_ATTESTATION_STALE_TIME * 2,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  });

  let valid = false;
  let decodedData: unknown[] = [];
  if (attestation) {
    if (
      isAddressEqual(attestation.recipient, address) &&
      attestation.expirationTime === 0n &&
      attestation.revocationTime === 0n
    ) {
      valid = true;
    }

    try {
      if (abiParams && abiParams.length > 0 && attestation.data.length > 2) {
        decodedData = decodeAbiParameters(abiParams, attestation.data);
      }
    } catch {
      // Decoding may fail for empty or malformed attestation data
    }
  }

  return {
    attestation: {
      attester: attestation?.attester as Hash,
      data: attestation?.data as Hash,
      decodedData: decodedData,
      id: attestation?.uid as Hash,
      recipient: attestation?.recipient as Hash,
      schemaId: attestation?.schema as Hash,
      valid: valid,
    },
    isLoading: Boolean(isLoadingUid) || Boolean(isLoadingAttestation),
  };
}
